/**
 * Browser-local persistence.
 *
 * Only the loadout genuinely needs to survive a reload: whether the player has
 * finished the tutorial, the parry spell they authored there, and the attack
 * spell they authored in the lobby. Everything else (match state, opponents,
 * spells forged between rounds) belongs to the server.
 */
import { safeParseSpell, type Spell } from '@spell/spell-schema';

const KEY = 'spell.profile.v1';

export interface StoredProfile {
  name: string;
  tutorialDone: boolean;
  parrySpell: Spell | null;
  /** The attack spell chosen in the lobby. Null until the player forges one. */
  attackSpell: Spell | null;
  updatedAt: number;
}

const EMPTY: StoredProfile = {
  name: '',
  tutorialDone: false,
  parrySpell: null,
  attackSpell: null,
  updatedAt: 0,
};

/**
 * A stored spell from an older schema version is dropped rather than trusted —
 * rebuilding one is a minute's work and better than crashing on boot.
 */
function readSpell(value: unknown): Spell | null {
  if (!value) return null;
  const check = safeParseSpell(value);
  return check.success ? check.data : null;
}

export function loadProfile(): StoredProfile {
  if (typeof localStorage === 'undefined') return { ...EMPTY };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as Partial<StoredProfile>;
    const parrySpell = readSpell(parsed.parrySpell);
    return {
      name: typeof parsed.name === 'string' ? parsed.name.slice(0, 20) : '',
      tutorialDone: Boolean(parsed.tutorialDone) && parrySpell !== null,
      parrySpell,
      attackSpell: readSpell(parsed.attackSpell),
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveProfile(patch: Partial<StoredProfile>): StoredProfile {
  const next: StoredProfile = { ...loadProfile(), ...patch, updatedAt: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Private-mode browsers just replay the tutorial; not fatal.
  }
  return next;
}

export function clearProfile() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
